// Astra Lite: Astra runs the free read at #see-your-odds for a visitor who
// has not signed in (#2549, ADR 0023; founder pick A v2, 2026-09-30).
//
// The flow is the shipped free read's, spoken by Astra: a paragraph (typed or
// said) becomes the picture, the visitor checks a school, and then asks the
// free questions about the read (how many is an admin setting, #2704; the
// server sends it as free_questions). A question Astra sends to the school box,
// and anything said in distress, never counts. At the limit, one card: Start
// free, carrying the picture and the checked schools into the new account
// (the same localStorage handoff TryHero uses, plus the parsed picture so
// nothing is re-typed or re-read).
//
// Measurement: the free read's own try_hero_* events keep their names and
// meaning (submitted, preview shown, refined, school checked, sign-in
// clicked, seed used for "Try this one"); Astra's steps add astra_lite_*.
// No event carries a word the visitor typed.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MagnifyingGlassIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import AstraAvatar from '../../concierge/AstraAvatar';
import { StartFree } from '../home/parts';
import AstraComposer, { MIN_CHARS, SAMPLES } from './AstraComposer';
import ReadCard from './ReadCard';
import { ASTRA_LITE as C, COUNTER_FROM, FOLLOWUP_CHIPS, FREE_QUESTIONS_FALLBACK, UNLOCK, firstQuestion } from '../../../content/astraLite';
import { clearReadCarry, holdReadForSignIn } from '../../../services/astraCarry';
import {
    astraAsk, astraClarify, astraPicture, astraRead, astraRefine, astraStartFree, HANDOFF_KEY, pageTimeForAsk, PICTURE_KEY,
} from '../../../services/astraLite';
import { track, EVENTS } from '../../../services/analytics';
import { REPLAY_PRIVATE_CLASS } from '../../../services/analyticsPrivacy';
import { entryProps, storeCarriedSchools } from '../../../utils/schoolEntry';
import { schoolByName } from '../../../utils/schoolSearch';
import { astraSchoolGreeting, astraSchoolUnderstood } from '../../../content/schoolEntry';
import MomentBox from '../../signup/MomentBox';
import useSignupMoments from '../../../hooks/useSignupMoments';
import { COPY as MOMENT_COPY, MOMENTS, admitsPhrase, targetsBucket } from '../../../content/signupMoments';
import { forgetMoments, holdMoment, momentPayoff } from '../../../services/signupMoments';
import { loadChatThread } from '../../../services/astraChatThread';
import { clearStoredRequest } from '../../../services/emailLinkSignIn';

// At a test-blind school the typing loop shows only students who name no
// SAT or ACT score (#3262, product invariant 4); all of them if none qualify.
const SCORE_RE = /\b(?:SAT|ACT|PSAT)\b/;
function blindSamples() {
    const kept = SAMPLES.filter((s) => !SCORE_RE.test(s.text));
    return kept.length ? kept : SAMPLES;
}

const FIELD_LABELS = [
    ['school', 'School'], ['grade', 'Grade'], ['location', 'Location'],
    ['gpa_unweighted', 'Unweighted GPA'], ['gpa_weighted', 'Weighted GPA'], ['sat_total', 'SAT'],
    ['act_composite', 'ACT'], ['ap_courses_count', 'AP/IB courses'], ['intended_major', 'Intended major'],
];
export const LIMIT_CTA = 'free_read_astra_limit';
const MAX_HISTORY = 6;
// How long the box a chip points at stays highlighted.
const UNLOCK_FLASH_MS = 1600;
// The counter shows only in the day's last five questions (founder, #2725),
// against the live admin number; never a hard-coded total.
export { COUNTER_FROM } from '../../../content/astraLite';
// The free questions a picture gets (an admin setting, #2704): the server's
// number when it sends a sane one, else what the page already had.
const freeQuestionsFrom = (data, fallback = FREE_QUESTIONS_FALLBACK) => (
    Number.isInteger(data?.free_questions) && data.free_questions > 0 ? data.free_questions : fallback);

// Links in a reply render as plain text: the server strips them already, and
// the model never authors a destination on this page.
// Images never render: one would load the moment it appeared (a tracking
// pixel an injected instruction could plant).
const MD_COMPONENTS = { a: ({ children }) => <span>{children}</span>, img: () => null };

// Conversation bubbles are blocked in session replay (#4475): the visitor's
// own words, and a reply that may be the 988 card, never record.
export function AstraBubble({ children, markdown, testid }) {
    return (
        <div className={`${REPLAY_PRIVATE_CLASS} flex items-start gap-2`} data-testid={testid}>
            <AstraAvatar size={28} className="mt-0.5" />
            <div className="inline-block max-w-[85%] rounded-2xl bg-[#F4F8F4] px-3.5 py-2.5 text-left text-sm leading-relaxed text-[#2C2C2C]">
                {markdown ? (
                    <div className="astra-md space-y-2 [&_em]:text-xs [&_em]:text-[#6B6B6B] [&_li]:my-1 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{markdown}</ReactMarkdown>
                    </div>
                ) : children}
            </div>
        </div>
    );
}

function VisitorBubble({ children }) {
    return (
        <div className={`${REPLAY_PRIVATE_CLASS} text-right`}>
            <span className="inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[#1A4D2E] px-3.5 py-2.5 text-left text-sm leading-relaxed text-white">
                {children}
            </span>
        </div>
    );
}

// The parse sometimes keeps a course's program in `type` and drops it from
// the name: "AP Calculus BC" typed, {name: 'Calculus BC', type: 'AP'} back
// (ask-astra re-record walk, 2026-10-09, #3686). Rigor counts `type`, so the
// read is right; the chip says the word again so the AP never looks dropped.
const PROGRAMS = { ap: 'AP', ib: 'IB', honors: 'Honors' };
export function withProgram(c) {
    if (!c || typeof c !== 'object' || typeof c.name !== 'string') return c;
    const prog = PROGRAMS[String(c.type || '').trim().toLowerCase()];
    const name = c.name.trim();
    if (!prog || !name || name.toLowerCase().startsWith(prog.toLowerCase() + ' ')) return c;
    return { ...c, name: `${prog} ${name}` };
}

function PictureChips({ fields }) {
    const chips = FIELD_LABELS
        .filter(([k]) => fields[k] !== undefined && fields[k] !== null && fields[k] !== '')
        .map(([k, label]) => ({ label, value: String(fields[k]) }));
    const acts = (fields.extracurriculars || []).slice(0, 5);
    // The courses and awards they typed are part of the picture the read uses
    // (rigor is scored off `courses`): shown, so nothing they said looks
    // dropped (tutorial capture 2026-10-03, #3027: AP Calc BC and AP CS A
    // were read but never shown).
    const named = (list) => (Array.isArray(list) ? list : [])
        .map((c) => (c && typeof c === 'object' ? c.name : c))
        .filter((n) => typeof n === 'string' && n.trim())
        .map((n) => n.trim());
    const courses = named((Array.isArray(fields.courses) ? fields.courses : []).map(withProgram));
    const awards = named(fields.awards).slice(0, 3);
    const shownCourses = courses.slice(0, 4).join(', ') + (courses.length > 4 ? ` +${courses.length - 4} more` : '');
    return (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="astra-picture">
            {chips.map((c) => (
                <span key={c.label} className="rounded-full border border-[#CFE0D2] bg-[#F4F8F3] px-3 py-1 text-sm text-[#1A4D2E]">
                    <span className="font-semibold">{c.label}:</span> {c.value}
                </span>
            ))}
            {courses.length > 0 && (
                <span data-testid="astra-picture-courses" className="rounded-full border border-[#CFE0D2] bg-[#F4F8F3] px-3 py-1 text-sm text-[#1A4D2E]">
                    <span className="font-semibold">Courses:</span> {shownCourses}
                </span>
            )}
            {awards.map((a) => (
                <span key={`aw-${a}`} className="rounded-full border border-[#E0DED8] bg-[#FAFAF8] px-3 py-1 text-sm text-[#4A4A4A]">
                    {a}
                </span>
            ))}
            {acts.map((a) => (
                <span key={`ec-${a.name}`} className="rounded-full border border-[#E0DED8] bg-[#FAFAF8] px-3 py-1 text-sm text-[#4A4A4A]">
                    {a.name}{a.role ? ` - ${a.role}` : ''}
                </span>
            ))}
        </div>
    );
}

// Google's mark, for the sign-in button (its own four colours, as Google's
// sign-in branding asks for). Decorative: the button's words name it.
function GoogleMark() {
    return (
        <svg viewBox="0 0 48 48" className="h-4 w-4 shrink-0" aria-hidden="true" focusable="false">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
    );
}

// The ask under a locked read (#3480, mock B1): what sign-in shows, named
// from THIS read by the server (astra_read_unlock.unlock_box), one Google
// button, and the small line on what comes along and what Google gives us.
// `flash`: the chip that points here just scrolled to it (#3480 walk,
// 2026-10-09: the chip opens THIS box, never a second one under a bubble).
const UnlockBox = React.forwardRef(function UnlockBox({ box, onUnlock, flash = false }, ref) {
    return (
        <div ref={ref} data-testid="astra-unlock" data-flash={flash ? 'on' : undefined}
            className={`rounded-xl border border-dashed p-4 text-left transition-shadow duration-300 motion-reduce:transition-none ${flash
                ? 'border-[#1A4D2E] bg-[#EAF3EC] shadow-[0_0_0_3px_rgba(26,77,46,0.25)]'
                : 'border-[#9DBBA6] bg-[#F4F9F5]'}`}>
            <p className="text-sm font-semibold text-[#1A4D2E]">{box.title}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[#4A4A4A]" data-testid="astra-unlock-items">
                {box.items.map((it) => <li key={it}>{it}</li>)}
            </ul>
            <button type="button" onClick={onUnlock} data-cta="astra_read_unlock" data-testid="astra-unlock-go"
                className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border border-[#DADCE0] bg-white px-4 py-2 text-sm font-semibold text-[#3C4043] hover:bg-[#F8F9FA] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30">
                <GoogleMark /> {UNLOCK.button}
            </button>
            <p className="mt-2 text-xs leading-snug text-[#6B6B6B]">{UNLOCK.small(box.short)}</p>
        </div>
    );
});

// Nothing a child (or a visitor in crisis) typed stays on this device
// (security review of #2725): the draft, the picture, the carried schools and
// the sign-in handoff all go, so a shared device never carries them into a
// new account at sign-in (utils/postSignIn.js reads the draft).
export function forgetVisitorDraft() {
    try {
        ['scratchpad_draft_anonymous', PICTURE_KEY, HANDOFF_KEY, 'try_hero_schools_anonymous']
            .forEach((k) => localStorage.removeItem(k));
    } catch { /* private mode */ }
    clearReadCarry();
    forgetMoments();
    // the copy kept beside an email link already sent holds the tab's carry
    // (the conversation, the moment's GPA and state): it goes too (#5195 review)
    clearStoredRequest();
}

function Honeypot({ value, onChange }) {
    // Invisible to people and to screen readers; a form-filling bot fills it.
    return (
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other"
            value={value} onChange={(e) => onChange(e.target.value)}
            style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }} />
    );
}

// `onUnavailable`: the route said its daily budget is spent; the page shows
// the shipped free read instead, so the ad landing never goes blank.
//
// `signedIn`: an allowlisted account dogfooding Astra Lite. A refusal (404)
// is then said in one honest line and the box stays: it never drops to the
// old free read mid-conversation (founder, 2026-10-01). A guest keeps the
// fallback.
//
// `school`: the entry a college page sent (utils/schoolEntry.js, #2608), or
// null. With one, the greeting names the school and the first read after the
// picture is that school's; the typing loop, the mic and the school box are
// unchanged. Without one, nothing here changes.
// `embedded` + `chatToken` (#2725): the open chat's "Make it about me". The
// same flow renders inside the chat's panel (no heading, no panel header of
// its own), and the picture upgrades the chat's conversation, so its record
// and the day's free questions carry on.
// `onClosed()`: an under-13 answer in the embedded flow ends the chat's
// conversation too (its token is forgotten; security review of #2725).
// `firstSchoolName` (#5229): the school the chat's nudge was about, by the
// name the answer spoke (the chat answer carries no id). It is resolved
// against the roster the picture brings back (utils/schoolSearch
// schoolByName, unique or nothing) and then read first, like a school the
// visitor named in the picture; an unresolved name changes nothing. The
// greeting stays the plain one: the school's testing policy is unknown here,
// and a test-blind school's visitor is never asked for scores (invariant 4).
// `composerSecondary` (#5237): an outline button beside Build my picture
// (AstraComposer `secondary`); the chat passes its "Sign in instead".
export default function AstraLite({ heading, sub, onStart, onUnavailable, school = null, signedIn = false,
    embedded = false, chatToken = null, onClosed, firstSchoolName = null, composerSecondary = null }) {
    const [hp, setHp] = useState('');
    const [phase, setPhase] = useState('write');       // write | building | picture
    const [notice, setNotice] = useState(null);        // a reply shown before any picture (crisis, under 13)
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState('');
    const [token, setToken] = useState(null);
    const [handoff, setHandoff] = useState(null);
    const [fields, setFields] = useState({});
    const [missing, setMissing] = useState([]);
    const [roster, setRoster] = useState([]);
    const [reads, setReads] = useState([]);           // newest first
    const [query, setQuery] = useState('');
    const [gpaDraft, setGpaDraft] = useState('');
    // A sanity question from the server (picture_checks): asked before any
    // school is read, answered with a choice or a number.
    const [clarify, setClarify] = useState(null);
    const [clarifyDraft, setClarifyDraft] = useState('');
    const [thread, setThread] = useState([]);          // {role, text, kind}
    const [total, setTotal] = useState(FREE_QUESTIONS_FALLBACK);
    const [left, setLeft] = useState(FREE_QUESTIONS_FALLBACK);
    const [limit, setLimit] = useState(false);
    // An under-13 answer ends the conversation (#2621): the token is dropped
    // and the box closes, so nothing more is asked or counted.
    const [closed, setClosed] = useState(false);
    const [asking, setAsking] = useState(false);
    const [draft, setDraft] = useState('');
    // The follow-up chips Jev picked for the last answer, from the server's own
    // closed list; null when the server sent none (the page's fixed three).
    const [nextChips, setNextChips] = useState(null);
    const [busy, setBusy] = useState(false);
    // The one school the visitor's own words asked about (the server resolves
    // it against the directory: `named_school`), read like a college page's
    // entry. Tutorial capture 2026-10-03 (#3027): "Where do I stand at UC San
    // Diego?" was answered with "Which school should I check?".
    const [namedSchool, setNamedSchool] = useState(null);
    const target = school || namedSchool;
    const shownRef = useRef(false);
    // #3480: the "What would help my chances" chips already tapped ({q, uid}:
    // the chip leaves the row; its school's sign-in counts as from 'chip'),
    // the box a chip just pointed at (briefly highlighted), each read's box
    // element, and the schools whose box has been counted as shown.
    const [chipUnlocks, setChipUnlocks] = useState([]);
    const [flashUid, setFlashUid] = useState(null);
    const unlockBoxes = useRef({});
    const unlockSeen = useRef(new Set());
    // Sign-up moments (#5195, signup_moments, founder pick A): each read's
    // box becomes the moment's box (the Targets count free, what a free
    // account adds, no provider named); `payoffs` holds each school's taste.
    const momentsOn = useSignupMoments();
    const [payoffs, setPayoffs] = useState({});
    // An answer lands whenever it comes while the box is mounted: tied to one
    // run of the effect, a second school checked mid-request dropped the
    // first one's answer and left it with no box at all (review of #5195).
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        if (!flashUid) return undefined;
        const t = setTimeout(() => setFlashUid(null), UNLOCK_FLASH_MS);
        return () => clearTimeout(t);
    }, [flashUid]);

    useEffect(() => {
        if (shownRef.current) return;
        shownRef.current = true;
        track(EVENTS.ASTRA_LITE_SHOWN, {});
    }, []);

    // The checked schools ride into sign-up (TryHeroCollegesPrompt offers to
    // add them), exactly as TryHero hands them over.
    useEffect(() => {
        if (!reads.length) return;
        storeCarriedSchools(reads, school);
    }, [reads, school]);

    // The moment's taste for each read, asked once per school and picture.
    useEffect(() => {
        if (!momentsOn) return;
        reads.forEach((r) => {
            const uid = r.university_id;
            if (!uid || payoffs[uid] !== undefined) return;
            setPayoffs((prev) => ({ ...prev, [uid]: null }));
            momentPayoff(fields, [uid], MOMENTS.astra_read.payoffs)
                .then((p) => { if (mounted.current) setPayoffs((prev) => ({ ...prev, [uid]: p || false })); });
        });
    }, [momentsOn, reads]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Each school's unlock box, counted once when it first shows.
    useEffect(() => {
        if (momentsOn) return;   // the moment's box counts itself (signup_moment)
        reads.forEach((r) => {
            if (!r.unlock || unlockSeen.current.has(r.university_id)) return;
            unlockSeen.current.add(r.university_id);
            track(EVENTS.ASTRA_READ_UNLOCK, { state: 'shown', schools_n: reads.length, from: 'card' });
        });
    }, [reads]);

    const ep = entryProps(school);

    const run = async (text, seedKey) => {
        const body = String(text || '').trim();
        if (body.length < MIN_CHARS || phase === 'building') return;
        if (seedKey) track(EVENTS.TRY_HERO_SEED_USED, { seed: seedKey, auto_ran: true, ...ep });
        track(EVENTS.TRY_HERO_SUBMITTED, { chars: body.length, ...ep });
        try { localStorage.setItem('scratchpad_draft_anonymous', body); } catch { /* private mode */ }
        if (school) storeCarriedSchools([], school);
        setPhase('building');
        setError(null);
        setNotice(null);
        // where a new conversation started (#3473): the landing page, or a
        // college page's "see your odds" link (?from=college_page)
        const data = await astraPicture(body, await pageTimeForAsk(), hp, chatToken,
            school?.from === 'college_page' ? 'college_page' : 'home');
        if (data.crisis || data.under_13) {
            forgetVisitorDraft();
            if (data.under_13) onClosed?.();
            setNotice(data.reply);
            setPhase('write');
            return;
        }
        if (!data.success) {
            // Spent budget, or a route that refuses this caller (404): a
            // signed-in account is told so in one line and keeps the box; a
            // guest gets the shipped free read in its place.
            if (data.status === 404 && signedIn) {
                setError(C.refused);
                setPhase('write');
                return;
            }
            if ((data.busy || data.status === 404) && onUnavailable) { onUnavailable(); return; }
            setError(data.status === 404 ? C.refused : (data.error || C.error));
            setPhase('write');
            return;
        }
        setSubmitted(body);
        setToken(data.token);
        setHandoff(data.handoff || null);
        setFields(data.fields || {});
        setMissing(data.missing || []);
        setClarify(data.clarify || null);
        if (data.clarify) track(EVENTS.ASTRA_LITE_CLARIFY, { check: data.clarify.id, state: 'asked' });
        setRoster(data.schools || []);
        // the school the visitor's own words named, else (#5229) the one the
        // chat's nudge was about, found on the roster by its name
        const spoken = data.named_school?.id && data.named_school?.name
            ? data.named_school : schoolByName(data.schools, firstSchoolName);
        const named = !school && spoken ? { id: spoken.id, name: spoken.name, shortName: spoken.name } : null;
        setNamedSchool(named);
        setReads([]);
        setThread([]);
        const t = freeQuestionsFrom(data);
        setTotal(t);
        setLeft(t);
        setLimit(false);
        setPhase('picture');
        try { localStorage.setItem(PICTURE_KEY, JSON.stringify({ text: body, fields: data.fields || {} })); } catch { /* private */ }
        track(EVENTS.TRY_HERO_PREVIEW_SHOWN, { fields: Object.keys(data.fields || {}).length, ...ep });
        // The school the visitor came for reads at once, on the new session's
        // token. A picture with no GPA waits for the GPA answer (submitGpa).
        const first = school || named;
        if (first && !(data.missing || []).includes('gpa') && !data.clarify) {
            await checkSchool({ id: first.id, name: first.name }, data.token, data.fields || {});
        }
    };

    const answerClarify = async (answer) => {
        if (!clarify || busy) return;
        setBusy(true);
        setError(null);
        const data = await astraClarify(token, fields, clarify.id, answer);
        let readSchool = null;
        if (data.success) {
            track(EVENTS.ASTRA_LITE_CLARIFY, { check: clarify.id, state: 'answered',
                answer: typeof answer === 'number' ? 'number' : answer });
            const next = data.fields || fields;
            setFields(next);
            setMissing(data.missing || []);
            setToken(data.token);
            setClarify(data.clarify || null);
            setClarifyDraft('');
            if (data.clarify) track(EVENTS.ASTRA_LITE_CLARIFY, { check: data.clarify.id, state: 'asked' });
            try { localStorage.setItem(PICTURE_KEY, JSON.stringify({ text: submitted, fields: next })); } catch { /* private */ }
            if (target && !data.clarify && !(data.missing || []).includes('gpa')
                && !reads.some((r) => r.university_id === target.id)) readSchool = next;
        } else {
            setError(data.status === 404 ? C.refused : (data.error || C.error));
        }
        setBusy(false);
        if (readSchool) await checkSchool({ id: target.id, name: target.name }, data.token, readSchool);
    };

    const submitGpa = async () => {
        const v = parseFloat(gpaDraft);
        if (Number.isNaN(v) || v <= 0 || v > 5 || busy) return;
        setBusy(true);
        track(EVENTS.TRY_HERO_REFINED, { field: 'gpa', ...ep });
        const data = await astraRefine(token, fields, v);
        let readSchool = null;
        if (data.success) {
            const next = data.fields || { ...fields, gpa_unweighted: v };
            setFields(next);
            setMissing(data.missing || []);
            setToken(data.token);
            setClarify(data.clarify || null);
            if (data.clarify) track(EVENTS.ASTRA_LITE_CLARIFY, { check: data.clarify.id, state: 'asked' });
            try { localStorage.setItem(PICTURE_KEY, JSON.stringify({ text: submitted, fields: next })); } catch { /* private */ }
            if (target && !data.clarify && !(data.missing || []).includes('gpa')
                && !reads.some((r) => r.university_id === target.id)) readSchool = next;
        } else {
            setError(data.status === 404 ? C.refused : (data.error || C.error));
        }
        setBusy(false);
        if (readSchool) await checkSchool({ id: target.id, name: target.name }, data.token, readSchool);
    };

    // `withToken`/`withFields`: the auto-read runs in the same tick the
    // picture (or the GPA answer) landed, before state has caught up.
    const checkSchool = async (opt, withToken = token, withFields = fields) => {
        if (busy || !opt) return;
        setBusy(true);
        setError(null);
        track(EVENTS.TRY_HERO_SCHOOL_CHECKED, { school: opt.id, ...ep });
        const data = await astraRead(withToken, withFields, opt.id);
        if (data.success && data.read) {
            // `locked` (#3480): the server kept the reasons; its box rides on the read.
            const read = data.locked && Array.isArray(data.locked.items) && data.locked.items.length
                ? { ...data.read, unlock: data.locked } : data.read;
            setReads((prev) => [read, ...prev.filter((r) => r.university_id !== read.university_id)]);
            setToken(data.token);
            setQuery('');
        } else if (!data.success) {
            setError(data.status === 404 ? C.refused : (data.error || C.error));
        }
        setBusy(false);
    };

    const ask = async (message, source) => {
        const q = String(message || '').trim().slice(0, 500);
        if (!q || asking || limit || closed) return;
        setAsking(true);
        setError(null);
        setDraft('');
        const history = thread.slice(-MAX_HISTORY).map((m) => ({ role: m.role, text: m.text }));
        setThread((t) => [...t, { role: 'visitor', text: q }]);
        const data = await astraAsk(token, fields, q, history, hp);
        setTotal((t) => freeQuestionsFrom(data, t));
        if (data.limit && !data.reply) {
            setLimit(true);
            setLeft(0);
            setThread((t) => t.slice(0, -1));
            track(EVENTS.ASTRA_LITE_LIMIT_REACHED, {});
        } else if (data.success && data.under_13) {
            setToken(null);
            setClosed(true);
            forgetVisitorDraft();
            onClosed?.();
            setThread((t) => [...t, { role: 'astra', text: data.reply, kind: 'answer', left: null }]);
        } else if (data.success && data.reply) {
            // The server refreshes the session token each turn: it carries what
            // the privacy layer hid, so the next turn hides it again (#2549).
            if (data.token) setToken(data.token);
            const kind = data.redirected ? 'redirect' : (data.crisis ? 'crisis' : 'answer');
            setThread((t) => [...t, { role: 'astra', text: data.reply, kind,
                left: typeof data.questions_left === 'number' ? data.questions_left : null }]);
            if (typeof data.questions_left === 'number') setLeft(data.questions_left);
            setNextChips(Array.isArray(data.chips) ? data.chips.filter((c) => typeof c === 'string').slice(0, 3) : null);
            // No crisis flag, ever (#4294): a 988 reply is counted only in the
            // server's content-free ledger, never in PostHog.
            track(EVENTS.ASTRA_LITE_QUESTION_ASKED, {
                source, counted: !!data.counted, redirected: !!data.redirected,
                left: typeof data.questions_left === 'number' ? data.questions_left : null,
            });
            if (data.limit) {
                setLimit(true);
                track(EVENTS.ASTRA_LITE_LIMIT_REACHED, {});
            }
        } else {
            setThread((t) => t.slice(0, -1));
            setDraft(q);
            setError(data.status === 404 ? C.refused : (data.error || C.error));
        }
        setAsking(false);
    };

    const startFree = () => {
        if (closed) return;
        if (school) track(EVENTS.TRY_HERO_SIGNIN_CLICKED, ep); else track(EVENTS.TRY_HERO_SIGNIN_CLICKED);
        track(EVENTS.ASTRA_LITE_START_FREE, { schools: reads.length });
        if (school) storeCarriedSchools(reads, school);
        try {
            localStorage.setItem('scratchpad_draft_anonymous', submitted);
            localStorage.setItem(PICTURE_KEY, JSON.stringify({ text: submitted, fields }));
            if (handoff) localStorage.setItem(HANDOFF_KEY, handoff);
        } catch { /* private mode */ }
        astraStartFree(token);
        onStart?.(LIMIT_CTA);
    };

    // "Sign up free to see it" (#3480; named no provider since #5154): the picture and THIS school
    // ride into the account (the Lite session token signs both; the server
    // re-checks it at the carry), then the page's own Google sign-in.
    const unlock = (read, from) => {
        if (closed || !read?.unlock) return;
        track(EVENTS.ASTRA_READ_UNLOCK, { state: 'clicked', schools_n: reads.length, from });
        holdReadForSignIn({ token, fields, schoolId: read.university_id, text: submitted, handoff, reads, entry: school });
        onStart?.('astra_read_unlock');
    };
    // A chip that needs the reasons ("What would help my chances at
    // <School>?") takes the visitor to that school's box, already on the
    // page under its read: no question bubble, no second box (founder's live
    // walk 2026-10-09 saw both). A question the visitor types is asked as
    // any other.
    // The moment's box for one read, or null. payoffs[uid]: undefined/null
    // while asked, false when none came back (then the box says only what the
    // read itself holds: the why, without a count).
    const momentFor = (r) => {
        if (!momentsOn || closed) return null;
        const p = payoffs[r.university_id];
        if (p === undefined || p === null) return null;
        const band = ['Safety', 'Target', 'Reach'].includes(r.band) ? r.band : null;
        const school = r.unlock?.short || r.name;
        const gpaShown = fields.gpa_unweighted ?? fields.gpa_weighted;
        const gpa = gpaShown != null ? String(gpaShown) : null;
        const chat = embedded ? loadChatThread() : null;
        const box = MOMENTS.astra_read.box({
            school, band, gpa, targets: p ? p.targets : null, why: Boolean(r.unlock),
            date: null, early: false, saved: false,
            thread: Boolean(chat && chat.thread.some((m) => m.role === 'visitor')) || thread.some((m) => m.role === 'visitor'),
        });
        const fixed = p && band && p.fixed_band === band
            ? MOMENT_COPY.fixedBand(school, band, admitsPhrase(r.band_line)) : null;
        return box ? { box, fixed, gpa, payoff: p } : null;
    };
    const goMoment = (r, m) => {
        if (closed) return;
        holdReadForSignIn({ token, fields, schoolId: r.university_id, text: submitted, handoff, reads, entry: school });
        holdMoment({ moment: 'astra_read', schoolIds: [r.university_id], gpa: m.gpa,
            targets: Boolean(m.payoff && m.payoff.targets && m.payoff.targets.n > 0) });
        onStart?.(MOMENTS.astra_read.entry, { title: m.box.button, lead: m.box.lead });
    };

    const unlockReadFor = (q) => {
        if (!String(q).startsWith(UNLOCK.chipPrefix)) return null;
        return reads.find((r) => r.unlock && String(q).includes(r.name)) || null;
    };
    const tapChip = (q) => {
        const r = unlockReadFor(q);
        if (!r) { ask(q, 'chip'); return; }
        setChipUnlocks((prev) => [...prev, { q, uid: r.university_id }]);
        setFlashUid(r.university_id);
        const el = unlockBoxes.current[r.university_id];
        const still = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
        el?.scrollIntoView?.({ behavior: still ? 'auto' : 'smooth', block: 'center' });
        // keyboard and screen-reader users land on the box's one action
        el?.querySelector('[data-testid="astra-unlock-go"], [data-testid="astra-moment-go"]')?.focus({ preventScroll: true });
        track(EVENTS.ASTRA_READ_UNLOCK, { state: 'shown', schools_n: reads.length, from: 'chip' });
    };

    const matches = useMemo(() => (query.trim().length >= 2
        ? roster.filter((s) => s.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 8)
        : []), [query, roster]);
    const gated = missing.includes('gpa');
    // The school line promises a read only while one is on its way or on
    // screen; a failed auto-read falls back to asking for a school (review).
    const schoolReadShown = target && (busy || reads.some((r) => r.university_id === target.id));
    const understoodLine = clarify ? C.understoodClarify
        : target && (gated || schoolReadShown)
            ? astraSchoolUnderstood(target.shortName, gated)
            : C.understood;
    const asked = new Set([...thread.filter((m) => m.role === 'visitor').map((m) => m.text),
        ...chipUnlocks.map((c) => c.q)]);
    const anyLocked = reads.some((r) => r.unlock);
    const chips = reads.length
        ? (thread.length ? (nextChips ?? FOLLOWUP_CHIPS) : [firstQuestion(reads[0], fields)]).filter((q) => !asked.has(q))
        : [];

    return (
        <div data-testid={embedded ? 'astra-lite-embedded' : 'try-hero'} className={embedded ? 'text-left' : 'mx-auto max-w-3xl px-4 text-center'}>
            {!embedded && <h2 className="font-serif text-3xl font-semibold text-[#1A4D2E] [text-wrap:balance] sm:text-5xl">{heading}</h2>}
            {!embedded && <p className="mx-auto mt-3 max-w-xl text-[#4A4A4A] [text-wrap:pretty]">{sub}</p>}
            <div data-testid="astra-lite" className={embedded ? 'relative text-left' : 'relative mt-8 rounded-2xl border border-[#E0DED8] bg-white text-left shadow-sm'}>
                <Honeypot value={hp} onChange={setHp} />
                {!embedded && (
                    <header className="flex items-center gap-2 rounded-t-2xl border-b border-[#E0DED8] bg-[#1A4D2E] px-4 py-3.5">
                        <AstraAvatar size={24} />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-white">{C.panelName}</p>
                            <p className="truncate text-[11px] text-[#C7E3CB]">{C.panelLine}</p>
                        </div>
                    </header>
                )}
                <div className="space-y-3 px-4 py-4 sm:px-5" aria-live="polite">
                    <AstraBubble testid={school ? 'astra-school-greeting' : undefined}>
                        {school ? astraSchoolGreeting(school.shortName, school.testBlind) : C.greeting}
                    </AstraBubble>
                    {phase !== 'picture' && (
                        <>
                            <p data-testid="astra-lite-can" className="pl-9 text-[11px] leading-snug text-[#6B6B6B]">{C.canLine}</p>
                            {notice && <AstraBubble markdown={notice} testid="astra-notice" />}
                            {phase === 'building' && <AstraBubble testid="astra-building">{C.building}</AstraBubble>}
                            <AstraComposer onRun={run} busy={phase === 'building'} secondary={composerSecondary}
                                samples={school?.testBlind ? blindSamples() : undefined} />
                            {error && <p role="alert" className="text-sm text-[#C05838]">{error}</p>}
                        </>
                    )}
                    {phase === 'picture' && (
                        <>
                            <VisitorBubble>{submitted}</VisitorBubble>
                            <AstraBubble>
                                <span className="font-semibold">{understoodLine}</span>
                                <PictureChips fields={fields} />
                            </AstraBubble>
                            <div className="space-y-3 pl-9">
                                {clarify ? (
                                    <div className="rounded-xl border border-[#CFE0D2] bg-[#F4F8F3] p-4" data-testid="astra-clarify">
                                        <p className={`${REPLAY_PRIVATE_CLASS} font-semibold text-[#2C2C2C]`}>{clarify.question}</p>
                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                            {clarify.input && (
                                                <>
                                                    <input data-testid="astra-clarify-input" type="number"
                                                        step={clarify.input.step} min={clarify.input.min} max={clarify.input.max}
                                                        aria-label={clarify.input.label} placeholder={clarify.input.label}
                                                        value={clarifyDraft} onChange={(e) => setClarifyDraft(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            const n = parseFloat(clarifyDraft);
                                                            if (e.key === 'Enter' && !Number.isNaN(n)) answerClarify(n);
                                                        }}
                                                        className="w-44 rounded-lg border border-[#E0DED8] bg-white px-3 py-2 text-sm" />
                                                    <button type="button" data-testid="astra-clarify-go" disabled={busy || Number.isNaN(parseFloat(clarifyDraft))}
                                                        onClick={() => answerClarify(parseFloat(clarifyDraft))}
                                                        className="min-h-[44px] rounded-lg bg-[#1A4D2E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
                                                        Got it
                                                    </button>
                                                </>
                                            )}
                                            {clarify.options.map((o) => (
                                                <button key={o.id} type="button" data-testid={`astra-clarify-${o.id}`} disabled={busy}
                                                    onClick={() => answerClarify(o.id)}
                                                    className={`min-h-[44px] rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40 ${clarify.input
                                                        ? 'text-[#1A4D2E] underline underline-offset-4'
                                                        : 'border border-[#1A4D2E] bg-white text-[#1A4D2E] hover:bg-[#E8F0E9]'}`}>
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : gated ? (
                                    <div className="rounded-xl border border-[#F0D5C8] bg-[#FCF7F4] p-4" data-testid="try-hero-gpa-ask">
                                        <p className="font-semibold text-[#2C2C2C]">{C.gpaAsk}</p>
                                        <p className="mt-1 text-sm text-[#6B6B6B]">{C.gpaHint}</p>
                                        <div className="mt-3 flex gap-2">
                                            <input data-testid="try-hero-gpa-input" type="number" step="0.01" min="0" max="5"
                                                aria-label="Your GPA" value={gpaDraft} onChange={(e) => setGpaDraft(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && submitGpa()} placeholder="3.8"
                                                className="w-28 rounded-lg border border-[#E0DED8] bg-white px-3 py-2 text-sm" />
                                            <button type="button" data-testid="try-hero-gpa-go" onClick={submitGpa} disabled={busy}
                                                className="min-h-[44px] rounded-lg bg-[#1A4D2E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
                                                Got it
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B6B6B]" aria-hidden="true" />
                                        <input data-testid="try-hero-school-input" type="text" value={query}
                                            aria-label="School" placeholder={anyLocked ? C.schoolPlaceholderFree : C.schoolPlaceholder}
                                            onChange={(e) => setQuery(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && matches[0] && checkSchool(matches[0])}
                                            className="w-full rounded-lg border border-[#E0DED8] bg-white px-3 py-3 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30" />
                                        {matches.length > 0 && (
                                            <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-[#E0DED8] bg-white shadow-lg" data-testid="try-hero-school-options">
                                                {matches.map((m) => (
                                                    <button key={m.id} type="button" onClick={() => checkSchool(m)}
                                                        className="block min-h-[44px] w-full px-4 py-2.5 text-left text-sm hover:bg-[#F4F8F3]">
                                                        {m.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {reads.map((r) => {
                                    const m = momentFor(r);
                                    return (
                                    <React.Fragment key={r.university_id}>
                                        <ReadCard read={r} />
                                        {m && m.fixed && (
                                            <p data-testid="astra-moment-fixed-band" className={`${REPLAY_PRIVATE_CLASS} text-sm font-semibold text-[#1A2E1F]`}>{m.fixed}</p>
                                        )}
                                        {m && (
                                            // the chip that needs the reasons scrolls here (tapChip)
                                            <div ref={(el) => { unlockBoxes.current[r.university_id] = el; }}>
                                                <MomentBox moment="astra_read" box={m.box} onGo={() => goMoment(r, m)} testid="astra-moment"
                                                    props={{ band: ['Safety', 'Target', 'Reach'].includes(r.band) ? r.band : null,
                                                        fixed_band: Boolean(m.fixed), schools_n: reads.length,
                                                        ...(m.payoff && m.payoff.targets ? { targets_n_bucket: targetsBucket(m.payoff.targets.n) } : {}) }} />
                                            </div>
                                        )}
                                        {!m && r.unlock && !closed && !momentsOn && (
                                            <UnlockBox box={r.unlock} flash={flashUid === r.university_id}
                                                ref={(el) => { unlockBoxes.current[r.university_id] = el; }}
                                                onUnlock={() => unlock(r, chipUnlocks.some((c) => c.uid === r.university_id) ? 'chip' : 'card')} />
                                        )}
                                    </React.Fragment>
                                    );
                                })}
                            </div>
                            {thread.map((m, i) => (m.role === 'visitor'
                                // eslint-disable-next-line react/no-array-index-key
                                ? <VisitorBubble key={i}>{m.text}</VisitorBubble>
                                : (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <React.Fragment key={i}>
                                        <AstraBubble markdown={m.text} testid={`astra-${m.kind}`} />
                                        {m.kind === 'redirect' && m.left != null && (
                                            <p data-testid="astra-redirect-note" className="pl-9 text-xs font-semibold text-[#4A6B53]">
                                                {C.redirectNote(m.left, total)}
                                            </p>
                                        )}
                                    </React.Fragment>
                                )))}
                            {asking && <AstraBubble testid="astra-thinking">{C.thinking}</AstraBubble>}
                            {error && <p role="alert" className="pl-9 text-sm text-[#C05838]">{error}</p>}
                        </>
                    )}
                </div>
                {phase === 'picture' && reads.length > 0 && !limit && !closed && (
                    <div className="space-y-3 border-t border-[#F0EEE8] px-4 py-4 sm:px-5">
                        {left <= COUNTER_FROM && (
                            <p data-testid="astra-guest-counter" aria-live="polite"
                                className={`text-xs font-semibold ${left <= 1 ? 'text-[#C05838]' : 'text-[#4A6B53]'}`}>
                                {C.counterLeft(left, total)}
                            </p>
                        )}
                        {chips.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {chips.map((q) => (
                                    <button key={q} type="button" disabled={asking} onClick={() => tapChip(q)}
                                        data-testid="astra-chip"
                                        className="min-h-[44px] rounded-full border border-[#CFE0D2] bg-white px-3 py-2 text-left text-sm font-medium text-[#1A4D2E] hover:bg-[#F0F7F1] disabled:opacity-50">
                                        {q}
                                    </button>
                                ))}
                            </div>
                        )}
                        <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); ask(draft, 'typed'); }}>
                            <textarea data-testid="astra-ask" rows={1} maxLength={500} value={draft}
                                aria-label={C.askPlaceholder} placeholder={C.askPlaceholder}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(draft, 'typed'); } }}
                                className="min-h-[44px] w-full resize-none rounded-xl border border-[#E0DED8] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30" />
                            <button type="submit" aria-label="Send" disabled={asking || !draft.trim()} data-testid="astra-send"
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1A4D2E] text-white disabled:opacity-40">
                                <PaperAirplaneIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </form>
                        <p data-testid="astra-notice-line" className="text-[11px] leading-snug text-[#6B6B6B]">{embedded ? C.canLine : `${C.canLine} ${C.notice}`}</p>
                    </div>
                )}
                {limit && (
                    <div className="space-y-3 border-t border-[#F0EEE8] px-4 py-4 sm:px-5">
                        <div data-testid="astra-guest-limit" className="rounded-2xl bg-[#1A4D2E] p-5 text-center sm:p-6">
                            <div className="flex items-center justify-center gap-2">
                                <AstraAvatar size={28} />
                                <p className="font-semibold text-white">{C.limitTitle}</p>
                            </div>
                            <p className="mx-auto mt-2 max-w-md text-sm text-[#C7E3CB]">{C.limitLine}</p>
                            <StartFree onStart={startFree} cta="free_read_astra_limit" tone="white" size="md" className="mt-4" />
                            <p className="mt-2 text-xs text-[#C7E3CB]">{C.limitSmall}</p>
                        </div>
                        {!embedded && <p className="text-[11px] leading-snug text-[#6B6B6B]">{C.notice}</p>}
                    </div>
                )}
                {/* Embedded, the open chat's own notice closes the panel; a
                    second one here was the duplicate the #2886 mocks found. */}
                {!embedded && (phase !== 'picture' || !reads.length) && (
                    <p data-testid="astra-lite-notice" className="border-t border-[#F0EEE8] px-4 py-3 text-[11px] leading-snug text-[#6B6B6B] sm:px-5">{C.notice}</p>
                )}
            </div>
        </div>
    );
}
